<template>
	<div v-if="showSecondStep" class="event-intro-popup-second__wrapper">
		<ScrollFade>
			<template #scrollContent>
				<div
					class="event-intro-popup-second"
					:data-event-type="attributes.type"
				>
					<div class="event-intro-popup-second__text">
						{{
							translate(
								`event.${attributes.type}_event.tutorial_1`,
							)
						}}
					</div>
					<video
						class="event-intro-popup-second__video"
						autoplay
						loop
						muted
						playsinline
					>
						<source
							:src="`${pathEventsHttp}/${attributes.type}/backgrounds/intro.mp4`"
							type="video/mp4"
						/>
					</video>
					<HorizontalDecorDivider :with-sides="true" />

					<div class="event-intro-popup-second__text">
						{{
							translate(
								`event.${attributes.type}_event.tutorial_2`,
							)
						}}
					</div>
					<div class="event-intro-popup-second__production-wrapper">
						<EventiumProductionBox
							v-for="i in attributes.type &&
							[
								EventCategory.EASTER,
								EventCategory.HALLOWEEN,
								EventCategory.PIRATES,
							].includes(attributes.type)
								? 2
								: 1"
							:key="`prod_box_${i}`"
							:rare="i === 2"
						/>
					</div>
					<HorizontalDecorDivider :with-sides="true" />

					<div class="event-intro-popup-second__text">
						{{
							translate(
								`event.${attributes.type}_event.tutorial_3`,
							)
						}}
					</div>
					<div class="event-intro-popup-second__upgrades" />
					<HorizontalDecorDivider :with-sides="true" />

					<div class="event-intro-popup-second__text">
						{{
							translate(
								`event.${attributes.type}_event.tutorial_4`,
							)
						}}
					</div>
					<div
						v-if="hasChests"
						class="event-intro-popup-second__chests"
					></div>
					<div
						class="event-intro-popup-second__milestones"
						v-else-if="milestonesIcons.length > 0"
					>
						<div
							:class="`building-icon icon-${milestonesIcons[0]}-60`"
						/>
						<div class="reward-box">
							<div class="reward-box__icon">
								<AppMainIcon
									:iconName="ResourceType.GEMS"
									:iconSize="40"
								/>
							</div>
						</div>
						<div
							:class="`building-icon icon-${milestonesIcons[1]}-60`"
						/>
					</div>
					<div
						class="event-intro-popup-second__pass"
						v-if="!hasChests"
					>
						<span class="event-intro-popup-second__pass--plus">
							+
						</span>
						<div class="event-intro-popup-second__text">
							{{
								translate(
									`event.${attributes.type}_event.tutorial_5`,
								).replace(
									"{pass}",
									translate(
										`offer.lok_${attributes.type}_pass.non_translatable`,
									),
								)
							}}
						</div>
						<AppTextButton
							class="pass-btn"
							:text="
								translate(
									`offer.lok_${attributes.type}_pass.non_translatable`,
								)
							"
							:secondText="
								translate(
									`event.${eventPassPurchased ? 'activated' : 'activate'}`,
								)
							"
							:secondFontSize="11"
							:type="StatusTypes.SECONDARY"
							:disabled="eventPassPurchased"
							@click="goToShop"
						/>
					</div>
					<AppTextButton
						class="close-btn"
						:text="translate('button.continue')"
						:type="StatusTypes.PRIMARY"
						@click="emit('closePopup')"
					/>
					<div class="event-intro-popup__page">2/2</div>
				</div>
			</template>
		</ScrollFade>
	</div>

	<div v-else class="event-intro-popup" :data-event-type="attributes.type">
		<div class="event-intro-popup__header">
			<div class="event-intro-popup__header-bg">
				<div class="event-intro-popup__header-bg-img" />
			</div>
			<div class="event-intro-popup__header-heading">
				<div class="heading-wrapper">
					<ShrinkText
						:text="
							translate(
								`event.${attributes.type}_event.title.non_translatable`,
							)
						"
						:fontSize="20"
						textClassList="event-intro-popup__header-ribbon-text"
					/>
				</div>
				<div class="event-intro-popup__header-ribbon" />
			</div>
			<AppIconButton
				class="event-intro-popup__close-button"
				:type="IconTypes.CLOSE"
				@click="emit('closePopup')"
			/>
		</div>
		<div class="event-intro-popup__content">
			<div class="event-intro-popup__text">
				<p
					v-for="key in introTextKeys"
					:key="key"
					class="event-intro-popup__text-item"
				>
					{{ translate(key) }}
				</p>
			</div>
			<AppTextButton
				class="close-btn"
				:text="translate('button.continue')"
				:type="buttonType"
				@click="continueAction(false)"
			/>
			<div
				v-if="
					attributes.type &&
					[
						EventCategory.FISHING,
						EventCategory.EASTER,
						EventCategory.FIREFEST,
						EventCategory.SOLAR,
						EventCategory.HUNTING,
						EventCategory.HALLOWEEN,
						EventCategory.PIRATES,
					].includes(attributes.type)
				"
				class="event-intro-popup__page light"
			>
				1/2
			</div>
		</div>
		<div class="event-intro-popup-decoration">
			<GoldenCorner bottom left />
			<GoldenCorner bottom right />
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import GoldenCorner from "@/components/SVG/GoldenCorner.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import { useEventStore } from "@/event";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import useSocketService from "@/hooks/useSocketService.ts";
import {
	OfferAnalyticsClickSource,
	OfferAnalyticsType,
} from "@/libs/SocketService.ts";
import { PaymentProvider } from "@/services/PaymentProvider.ts";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useShopStore from "@/store/useShopStore";
import { EventCategory, IconTypes, SkinTypes, StatusTypes } from "@/types/ui";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import { pathEvents } from "@/utils/url";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import EventiumProductionBox from "./EventiumProductionBox.vue";

const emit = defineEmits(["closePopup"]);

const showSecondStep = ref<boolean>(false);

const popupStore = usePopupStore();
const shopStore = useShopStore();

const eventStore = useEventStore();
const { attributes, eventPassPurchased } = storeToRefs(eventStore);

const pathEventsHttp = computed((): string => {
	// zmení iba assets:// na začiatku
	return pathEvents.replace(/^assets:\/\//, "https://");
});

const hasChests = computed((): boolean => {
	if (!attributes.value.type) return false;

	return [EventCategory.SOLAR, EventCategory.PIRATES].includes(
		attributes.value.type,
	);
});

const milestonesIcons = computed((): Array<string> => {
	if (attributes.value.type === EventCategory.FISHING) {
		return [
			getTileIconName(FrameName.FORTRESS),
			getTileIconName(FrameName.SILVER_MINE),
		];
	}
	if (attributes.value.type === EventCategory.EASTER) {
		return [
			getTileIconName(FrameName.RABBIT_FARM),
			getTileIconName(FrameName.UNIVERSAL_WAREHOUSE),
		];
	}
	if (attributes.value.type === EventCategory.FIREFEST) {
		return [
			getTileIconName(FrameName.ENKINDLED_HOUSEHOLD),
			getTileIconName(FrameName.UNIVERSAL_WAREHOUSE),
		];
	}
	if (attributes.value.type === EventCategory.HUNTING) {
		return [
			getTileIconName(FrameName.SHEPHERDS_LODGE),
			getTileIconName(FrameName.SILVER_MINE),
		];
	}
	if (attributes.value.type === EventCategory.HALLOWEEN) {
		return [
			getTileIconName(FrameName.HAUNTED_MANSION),
			getTileIconName(FrameName.UNIVERSAL_WAREHOUSE),
		];
	}
	return [];
});

const buttonType = computed((): StatusTypes => {
	if (
		attributes.value.type &&
		[
			EventCategory.FISHING,
			EventCategory.EASTER,
			EventCategory.FIREFEST,
			EventCategory.SOLAR,
			EventCategory.HUNTING,
			EventCategory.HALLOWEEN,
			EventCategory.PIRATES,
		].includes(attributes.value.type)
	) {
		return StatusTypes.WARNING;
	}
	return StatusTypes.PRIMARY;
});

const introTextKeys = computed((): Array<string> => {
	switch (attributes.value.type) {
		case EventCategory.EASTER:
			return [`event.${attributes.value.type}_event.event_flavor_1`];

		default:
			return [
				`event.${attributes.value.type}_event.event_flavor_1`,
				`event.${attributes.value.type}_event.event_flavor_2`,
			];
	}
});

const continueAction = (isSecondStep: boolean): void => {
	if (
		attributes.value.type &&
		[
			EventCategory.FISHING,
			EventCategory.EASTER,
			EventCategory.FIREFEST,
			EventCategory.SOLAR,
			EventCategory.HUNTING,
			EventCategory.HALLOWEEN,
			EventCategory.PIRATES,
		].includes(attributes.value.type) &&
		!isSecondStep
	) {
		// Smoothly change internal state (without unmounting the component)
		showSecondStep.value = true;

		// Dispatch action to dynamically update skin & outer window header
		popupStore.updatePopup({
			type: PopupType.INFO,
			heading: translate("event.how_to_play"),
			skin: SkinTypes.LIGHT,
			width: 24.5,
			maxHeight: 35,
			isPaddingless: true,
			hasOverlap: false,
			hasOuterBar: false,
		});
	} else {
		emit("closePopup");
	}
};

const goToShop = (): void => {
	popupStore.hidePopup();
	const storeId = attributes.value.event_pass_store_id;

	if (!storeId) return;

	useSocketService().emitOfferAnalyticsSave({
		type: OfferAnalyticsType.OFFER_CLICK,
		clickSource: OfferAnalyticsClickSource.POPUP ?? "UNKNOWN",
		storeId: storeId ?? "UNKNOWN",
		offerName: shopStore.getItemByStoreId(storeId)?.name ?? "UNKNOWN",
	});

	PaymentProvider.openPopup(
		storeId,
		{
			clickSource: OfferAnalyticsClickSource.POPUP,
		},
		true,
	);
};
</script>

<style lang="scss" scoped>
.event-intro-popup {
	position: relative;
	border-radius: 0.15rem;
	background: linear-gradient(
		to bottom,
		rgb(218, 171, 44) 0%,
		rgb(240, 212, 133) 50%,
		rgb(237, 180, 28) 100%
	);
	padding: 0.125rem;
	width: 100%;
	@include flex-col-start;
	min-height: 10rem;
	&__page {
		margin-bottom: 0.5rem;
		text-align: center;
		&.light {
			color: #fff3dd;
		}
	}
	&-second {
		padding: 1rem;
		max-height: 30rem;
		@include flex-col-start;
		gap: 0.75rem;
		&__wrapper {
			background-image: linear-gradient(to top, #af8f53, #f2dcb5);
			padding-bottom: 0.5rem;
			.pass-btn {
				padding: 0.1rem;
				max-width: 9.6rem;
			}
		}
		&__text {
			text-align: center;
		}
		&__video {
			flex-shrink: 0;
			width: 21.5rem;
			height: 16.563rem;
		}
		&__upgrades {
			flex-shrink: 0;
			width: 21.5rem;
			height: 5.563rem;
		}
		&__production {
			&-wrapper {
				@include flex-row-center;
				gap: 0.25rem;
			}
		}
		&__chests {
			flex-shrink: 0;
			width: 19.375rem;
			height: 7.063rem;
		}
		&__milestones {
			flex-shrink: 0;
			border-width: 0.125rem;
			border-style: solid;
			border-image-source: linear-gradient(
				90deg,
				rgba(255, 231, 165, 0) 0%,
				rgba(255, 231, 165, 0.8) 25%,
				rgba(255, 231, 165, 0.8) 75%,
				rgba(255, 231, 165, 0) 100%
			);
			border-image-slice: 1;
			background-image: linear-gradient(
				90deg,
				rgba(18, 54, 17, 0) 0%,
				rgba(18, 54, 17, 0.8) 25%,
				rgba(18, 54, 17, 0.8) 75%,
				rgba(18, 54, 17, 0) 100%
			);

			width: 100%;
			@include flex-row-center;
			gap: 0.75rem;
			.building-icon {
				position: relative;
				top: -0.325rem;
			}
		}

		&__pass {
			@include flex-col-center;
			gap: 0.5rem;
			&--plus {
				margin-bottom: 1.5rem;
				color: #f4d278;
				font-weight: bold;
				font-size: 3rem;
				line-height: 0rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}

		:deep(.horizontal-decor-sides-cracked-bg__path) {
			fill: #372615;
		}
		&[data-event-type="fishing"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "fishing/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
		}

		&[data-event-type="firefest"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "firefest/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
			.event-intro-popup-second__milestones {
				border-image-source: linear-gradient(
					90deg,
					rgba(255, 231, 165, 0) 0%,
					rgba(255, 231, 165, 0.8) 25%,
					rgba(255, 231, 165, 0.8) 75%,
					rgba(255, 231, 165, 0) 100%
				);
				background-image: linear-gradient(
					90deg,
					rgba(169, 41, 7, 0) 0%,
					rgba(169, 41, 7, 0.8) 25%,
					rgba(169, 41, 7, 0.8) 75%,
					rgba(169, 41, 7, 0) 100%
				);
			}
			.reward-box__icon {
				@include background(
					url($path-events + "firefest/backgrounds/reward-box-event.avif"),
					contain,
					center,
					no-repeat
				);
			}
		}

		&[data-event-type="easter"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "easter/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
			:deep(.eventium-box-background) {
				filter: drop-shadow(
					0.044rem 0.14rem 0.093rem rgba(0, 0, 0, 0.7)
				);
			}
		}

		&[data-event-type="halloween"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "halloween/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
			.event-intro-popup-second__milestones {
				border-image-source: linear-gradient(
					90deg,
					rgba(255, 231, 165, 0) 0%,
					rgba(255, 231, 165, 0.8) 25%,
					rgba(255, 231, 165, 0.8) 75%,
					rgba(255, 231, 165, 0) 100%
				);
				background-image: linear-gradient(
					90deg,
					rgba(52, 29, 113, 0) 0%,
					rgba(52, 29, 113, 0.8) 25%,
					rgba(52, 29, 113, 0.8) 75%,
					rgba(52, 29, 113, 0) 100%
				);
			}
			:deep(.eventium-box-background) {
				filter: drop-shadow(
					0.044rem 0.14rem 0.093rem rgba(0, 0, 0, 0.7)
				);
			}
		}

		&[data-event-type="solarfair"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "solarfair/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
			.event-intro-popup-second__chests {
				@include background(
					url($path-events + "solarfair/backgrounds/chests-welcome-screen.avif"),
					contain,
					center,
					no-repeat
				);
			}
			:deep(.eventium-box-background) {
				filter: drop-shadow(
					0.044rem 0.14rem 0.093rem rgba(0, 0, 0, 0.7)
				);
			}
		}
		&[data-event-type="pirates"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "pirates/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
			.event-intro-popup-second__chests {
				@include background(
					url($path-events + "pirates/backgrounds/chests-welcome-screen.avif"),
					contain,
					center,
					no-repeat
				);
			}
			:deep(.eventium-box-background) {
				filter: drop-shadow(
					0.044rem 0.14rem 0.093rem rgba(0, 0, 0, 0.7)
				);
			}
		}
		&[data-event-type="hunting"] {
			.event-intro-popup-second__upgrades {
				@include background(
					url($path-events + "hunting/backgrounds/upgrades-img.avif"),
					contain,
					center,
					no-repeat
				);
			}
		}

		.reward-box {
			&__icon {
				@include background(
					url($path-events + "christmas/backgrounds/reward-box-event.avif"),
					contain,
					center,
					no-repeat
				);
				width: 3.7rem;
				height: 3.7rem;
				@include flex-row-center;
				filter: drop-shadow(
					0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6)
				);
			}

			&__value {
				// NOTE: position + bottom to prevent boxes from shifting differently from the ‘+’ sign
				position: absolute;
				bottom: -1.5rem;
				color: #fff3dd;
				font-weight: 800;
				font-size: 1.188rem;
				line-height: 1.188rem;
				text-align: center;
				text-shadow: 0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6);
				white-space: nowrap;
			}
		}
	}

	&__header {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 11.625rem;

		&-bg {
			display: flex;
			position: absolute;
			top: 0;
			left: 0;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			width: 100%;
			height: 100%;
			overflow: hidden;
			overflow-x: clip;
			overflow-y: visible;

			&-img {
				margin-top: -2.813rem;
				width: 24.5rem;
				height: 13.375rem;
			}
		}

		&-heading {
			position: absolute;
			bottom: -1.75rem;
			left: 50%;
			transform: translateX(-50%);
		}

		&-ribbon {
			position: relative;
			filter: drop-shadow(0 0.5rem 0.75rem rgba(0, 0, 0, 0.8));
			width: 25.625rem;
			height: 3.875rem;
		}

		.heading-wrapper {
			position: absolute;
			top: calc(50% - 0.313rem);
			left: 50%;
			transform: translate(-50%, -50%);
			z-index: 1;
			width: 90%;

			:deep(.text-container) {
				.event-intro-popup__header-ribbon-text {
					color: #f4d278;
					font-weight: 800;
					text-align: center;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					text-transform: uppercase;
				}
			}
		}

		:deep(.app-button) {
			top: -0.375rem;
			right: -0.375rem;
			background-image: linear-gradient(to top, #181a1d, #303239);
			width: 1.875rem;
			height: 1.875rem;

			&.event-intro-popup__header-btn--visibility {
				position: absolute;
				right: unset;
				left: -0.375rem;
				z-index: 1;
				box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
				border-width: 0.075rem;
				border-style: solid;
				border-image-source: linear-gradient(to top, #bd9032, #f0d386);
				border-image-slice: 1;
				background-image: linear-gradient(to top, #181a1d, #303239);
			}
		}
	}

	&__content {
		position: relative;
		flex: 1 1 0;
		z-index: 0;

		padding: 2.75rem 1.125rem 1.25rem;
		width: 100%;
		@include flex-col-center;
		gap: 2rem;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			z-index: 0;
			// NOTE: Color-burn blending is better for dark colors
			mix-blend-mode: color-burn;
			background: repeat center;
			@include background(
				url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
				40%,
				center,
				repeat
			);
			width: 100%;
			height: 100%;
			content: "";
		}

		.event-intro-popup__text {
			@include flex-col-center;
			gap: 1rem;

			&-item {
				color: #f2dcb5;
				font-size: 0.875rem;
				line-height: 1.25rem;
				text-align: center;
			}
		}
	}

	&-decoration {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 100%;
		pointer-events: none;

		:deep(.golden-corner) {
			width: 1.5rem;
			height: 1.5rem;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
	}

	&__close-button {
		position: absolute;
		top: -1rem;
		right: -1rem;
	}
	&[data-event-type="christmas"] {
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "christmas/backgrounds/christmas-event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "christmas/backgrounds/christmas-event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__content {
			background: linear-gradient(
				to top,
				rgba(79, 5, 9, 1) 0%,
				rgba(152, 14, 20, 1) 100%
			);
		}
	}
	&[data-event-type="fishing"] {
		.event-intro-popup__content {
			background: linear-gradient(
				0deg,
				rgba(5, 48, 63, 1) 0%,
				rgba(16, 122, 161, 1) 100%
			);
		}
		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "fishing/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "fishing/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}

	&[data-event-type="easter"] {
		.event-intro-popup__page {
			&.light {
				color: #f2dcb5;
			}
		}
		.event-intro-popup__content {
			gap: 0.75rem;
			background: linear-gradient(
				0deg,
				rgba(44, 49, 20, 1) 0%,
				rgba(91, 104, 29, 1) 100%
			);
			padding-bottom: 0rem;
		}

		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "easter/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "easter/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="halloween"] {
		.event-intro-popup__page {
			&.light {
				color: #f2dcb5;
			}
		}
		.event-intro-popup__content {
			gap: 0.75rem;
			background: linear-gradient(
				0deg,
				rgba(26, 15, 50, 1) 0%,
				rgba(87, 67, 143, 1) 100%
			);
			padding-bottom: 0rem;
		}

		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "halloween/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "halloween/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="firefest"] {
		.event-intro-popup__page {
			&.light {
				color: #f2dcb5;
			}
		}
		.event-intro-popup__content {
			gap: 0.75rem;
			background: linear-gradient(
				0deg,
				rgba(67, 24, 13, 1) 0%,
				rgba(89, 27, 10, 1) 100%
			);
			padding-bottom: 0rem;
		}

		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "firefest/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "firefest/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="solarfair"] {
		.event-intro-popup__page {
			&.light {
				color: #f2dcb5;
			}
		}
		.event-intro-popup__content {
			gap: 0.75rem;
			background: linear-gradient(
				0deg,
				rgba(101, 60, 17, 1) 0%,
				rgba(196, 139, 59, 1) 100%
			);
			padding-bottom: 0rem;
		}

		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "solarfair/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "solarfair/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="pirates"] {
		.event-intro-popup__page {
			&.light {
				color: #f2dcb5;
			}
		}
		.event-intro-popup__content {
			gap: 0.75rem;
			background: linear-gradient(
				0deg,
				rgba(17, 39, 62, 1) 0%,
				rgba(40, 108, 183, 1) 100%
			);
			padding-bottom: 0rem;
		}

		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "pirates/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "pirates/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="hunting"] {
		.event-intro-popup__content {
			background: linear-gradient(
				0deg,
				rgba(8, 42, 6, 1) 0%,
				rgba(36, 115, 35, 1) 100%
			);
		}
		.event-intro-popup__header-ribbon {
			@include background(
				url($path-events + "hunting/backgrounds/event-intro-header-ribbon.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-intro-popup__header-bg-img {
			@include background(
				url($path-events + "hunting/backgrounds/event-intro-header-bg.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
}
</style>
